import { FC } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAbbreviationBadge,
  MiButton,
  MiCard,
  MiList,
  MiLoader,
  MiStackedTypography,
} from "@miplatform/components";
import { ButtonVariants, IHome, LoaderType } from "@miplatform/types";
import { mdiFormatListBulleted, mdiPlus } from "@mdi/js";
import { Box } from "@mui/material";
import { HomeInformation } from "../../../components/OrderTemplateSelection/HomeInformation/HomeInformation";
import { CustomWorkTemplateBase } from "./CreateWorkOrderDrawer.constants";

interface WorkOrderTemplateSelectionProps {
  home: IHome;
  templates: CustomWorkTemplateBase[];
  isLoading?: boolean;
  onSelectTemplate: (template: CustomWorkTemplateBase) => void;
  onBack: () => void;
}

export const WorkOrderTemplateSelection: FC<
  WorkOrderTemplateSelectionProps
> = ({ home, templates, isLoading = false, onSelectTemplate, onBack }) => {
  const { theme, classes } = useStyles();

  return (
    <Box className={classes.root}>
      <HomeInformation home={home} onRightIconClick={onBack} />
      <MiCard
        title="Work Orders"
        leftIcon={mdiFormatListBulleted}
        data-testid="work-orders-list"
        containerStyles={{ position: "relative", padding: theme.spacing(2) }}
      >
        <MiLoader
          isLoading={isLoading}
          type={LoaderType.Linear}
          style={{ position: "absolute", top: 0, left: 0, right: 0 }}
        />
        <MiList
          viewType="card"
          hideListPagination
          data={templates}
          cardProps={{
            leftCardProps: {
              leftWidth: "auto",
            },
            renderTitle: ({
              name,
              abbreviation,
              color,
              orderTemplateName,
            }: CustomWorkTemplateBase) => (
              <Box
                className={classes.titleContainer}
                sx={{
                  width: "auto",
                  minWidth: 0,
                }}
              >
                <MiAbbreviationBadge
                  abbreviation={abbreviation as string}
                  backgroundColor={color}
                />

                <MiStackedTypography
                  title={name}
                  description={orderTemplateName || "--"}
                  titleFontSize={theme.fontSize.medium}
                  descriptionFontSize={theme.fontSize.small}
                  titleProps={{
                    fontWeight: theme.fontWeight.bold,
                    color: theme.palette.cyan.dark,
                  }}
                  descriptionProps={{
                    color: theme.palette.grey.medium,
                  }}
                />
              </Box>
            ),
            renderItemActions: (template: CustomWorkTemplateBase) => (
              <MiButton
                title="Create"
                startIcon={{
                  path: mdiPlus,
                  iconSize: 0.7,
                }}
                variant={ButtonVariants.Contained}
                className={classes.scheduleButton}
                size="small"
                onClick={() => {
                  onSelectTemplate(template);
                }}
              />
            ),
          }}
        />
      </MiCard>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    padding: theme.spacing(0, 3),
    paddingTop: theme.spacing(2),
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: 0,
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginLeft: theme.spacing(1),
  },
  placeholderContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    height: 150,
  },
  placeholderText: {
    fontWeight: theme.fontWeight.medium,
    fontSize: theme.fontSize.xlarge,
  },
  placeholderSecondaryText: {
    fontSize: theme.fontSize.medium,
    color: theme.palette.grey.medium,
  },
  scheduleButton: {
    padding: theme.spacing(0.5, 4),
    borderRadius: theme.spacing(1),
  },
}));
